/**
 * RevisionComparator Component
 * Displays a table of changes between two quotation revisions
 * Supports comparing:
 * - Two different quotations (from list selection)
 * - Current quotation vs related revisions
 */
import { useState, useEffect, useCallback } from "react";
import {
  GitCompare,
  ArrowRight,
  Plus,
  Minus,
  Edit3,
  Package,
  DollarSign,
  User,
  Eye,
  Calculator,
  Truck,
  Shield,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  FileText
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Category icons and labels
const CATEGORY_CONFIG = {
  info: { icon: User, label: "Información del Proyecto", color: "blue" },
  items: { icon: Package, label: "Items / Productos", color: "purple" },
  installation: { icon: DollarSign, label: "Instalación", color: "green" },
  pdr: { icon: Shield, label: "PDR", color: "orange" },
  freight: { icon: Truck, label: "Flete Internacional", color: "cyan" },
  otros: { icon: MoreHorizontal, label: "Otros", color: "violet" },
  visibility: { icon: Eye, label: "Visibilidad de Secciones", color: "gray" },
  totals: { icon: Calculator, label: "Totales", color: "emerald" },
};

// Change type badges
const CHANGE_TYPE_CONFIG = {
  added: { label: "Agregado", color: "bg-green-100 text-green-700 border-green-200", icon: Plus },
  removed: { label: "Eliminado", color: "bg-red-100 text-red-700 border-red-200", icon: Minus },
  modified: { label: "Modificado", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Edit3 },
};

const RevisionComparator = ({
  isOpen,
  onClose,
  quotationId = null,
  quotation1 = null,
  quotation2 = null,
  allQuotations = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [relatedQuotations, setRelatedQuotations] = useState([]);
  const [selectedQuotation1, setSelectedQuotation1] = useState(quotation1?.id || "");
  const [selectedQuotation2, setSelectedQuotation2] = useState(quotation2?.id || "");
  const [expandedCategories, setExpandedCategories] = useState({
    info: true,
    items: true,
    installation: true,
    pdr: true,
    freight: true,
    otros: true,
    visibility: false,
    totals: true,
  });

  // Load related quotations when opened with a specific quotation
  useEffect(() => {
    if (isOpen && quotationId) {
      loadRelatedQuotations(quotationId);
      setSelectedQuotation1(quotationId);
    }
  }, [isOpen, quotationId]);

  // Set initial selections from props
  useEffect(() => {
    if (quotation1?.id) setSelectedQuotation1(quotation1.id);
    if (quotation2?.id) setSelectedQuotation2(quotation2.id);
  }, [quotation1, quotation2]);

  const loadRelatedQuotations = async (id) => {
    try {
      const response = await api.get(`${API}/quotations/${id}/related`);
      setRelatedQuotations(response.data.related || []);
    } catch (error) {
      console.error("Error loading related quotations:", error);
    }
  };

  const performComparison = useCallback(async () => {
    if (!selectedQuotation1 || !selectedQuotation2) {
      toast.error("Selecciona dos cotizaciones para comparar");
      return;
    }

    if (selectedQuotation1 === selectedQuotation2) {
      toast.error("Selecciona dos cotizaciones diferentes");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`${API}/quotations/compare`, {
        quotation_id_1: selectedQuotation1,
        quotation_id_2: selectedQuotation2,
      });
      setComparison(response.data);
    } catch (error) {
      console.error("Error comparing quotations:", error);
      toast.error("Error al comparar cotizaciones");
    } finally {
      setLoading(false);
    }
  }, [selectedQuotation1, selectedQuotation2]);

  // Auto-compare when both selections are made
  useEffect(() => {
    if (selectedQuotation1 && selectedQuotation2 && selectedQuotation1 !== selectedQuotation2) {
      performComparison();
    }
  }, [selectedQuotation1, selectedQuotation2, performComparison]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const groupChangesByCategory = (changes) => {
    const grouped = {};
    for (const change of changes) {
      const category = change.category || "info";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(change);
    }
    return grouped;
  };

  // Get quotation options for selectors
  const quotationOptions = allQuotations.length > 0 
    ? allQuotations 
    : [...relatedQuotations];

  // Add current quotation to options if not already there
  if (quotationId && !quotationOptions.find(q => q.id === quotationId)) {
    const currentFromList = allQuotations.find(q => q.id === quotationId);
    if (currentFromList) {
      quotationOptions.unshift(currentFromList);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <GitCompare className="w-5 h-5 text-blue-600" />
            Comparador de Revisiones
          </DialogTitle>
          <DialogDescription>
            Compara los cambios entre dos versiones de cotización
          </DialogDescription>
        </DialogHeader>

        {/* Selection Controls */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border">
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Cotización Base (Anterior)
            </label>
            <Select value={selectedQuotation1} onValueChange={setSelectedQuotation1}>
              <SelectTrigger className="bg-white" data-testid="compare-select-1">
                <SelectValue placeholder="Seleccionar cotización..." />
              </SelectTrigger>
              <SelectContent>
                {quotationOptions.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    <span className="font-mono text-xs">{q.code}</span>
                    <span className="mx-2">-</span>
                    <span className="text-sm">{q.name?.substring(0, 30)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ArrowRight className="w-6 h-6 text-slate-400 flex-shrink-0 mt-5" />

          <div className="flex-1">
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Cotización Nueva (Actual)
            </label>
            <Select value={selectedQuotation2} onValueChange={setSelectedQuotation2}>
              <SelectTrigger className="bg-white" data-testid="compare-select-2">
                <SelectValue placeholder="Seleccionar cotización..." />
              </SelectTrigger>
              <SelectContent>
                {quotationOptions.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    <span className="font-mono text-xs">{q.code}</span>
                    <span className="mx-2">-</span>
                    <span className="text-sm">{q.name?.substring(0, 30)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={performComparison}
            disabled={loading || !selectedQuotation1 || !selectedQuotation2}
            className="mt-5"
            data-testid="compare-btn"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <GitCompare className="w-4 h-4 mr-2" />
            )}
            Comparar
          </Button>
        </div>

        {/* Comparison Results */}
        {comparison && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Summary Header */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100 mb-4">
              <div className="flex items-center gap-6">
                <div className="text-sm">
                  <span className="text-slate-500">Comparando:</span>
                  <span className="ml-2 font-mono font-medium text-blue-700">
                    {comparison.quotation_1?.code}
                  </span>
                  <ArrowRight className="w-4 h-4 inline mx-2 text-slate-400" />
                  <span className="font-mono font-medium text-blue-700">
                    {comparison.quotation_2?.code}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-white">
                  <span className="text-slate-500 mr-1">Total:</span>
                  <span className="font-bold">{comparison.summary?.total_changes || 0}</span>
                </Badge>
                {comparison.summary?.added > 0 && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <Plus className="w-3 h-3 mr-1" />
                    {comparison.summary.added}
                  </Badge>
                )}
                {comparison.summary?.removed > 0 && (
                  <Badge className="bg-red-100 text-red-700 border-red-200">
                    <Minus className="w-3 h-3 mr-1" />
                    {comparison.summary.removed}
                  </Badge>
                )}
                {comparison.summary?.modified > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                    <Edit3 className="w-3 h-3 mr-1" />
                    {comparison.summary.modified}
                  </Badge>
                )}
              </div>
            </div>

            {/* Changes Table */}
            {comparison.changes?.length > 0 ? (
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-3">
                  {Object.entries(groupChangesByCategory(comparison.changes)).map(
                    ([category, changes]) => {
                      const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.info;
                      const IconComponent = config.icon;
                      const isExpanded = expandedCategories[category];

                      return (
                        <Collapsible
                          key={category}
                          open={isExpanded}
                          onOpenChange={() => toggleCategory(category)}
                        >
                          <CollapsibleTrigger asChild>
                            <div
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors hover:bg-slate-100 bg-${config.color}-50 border border-${config.color}-100`}
                              data-testid={`category-${category}`}
                            >
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                                <IconComponent className={`w-4 h-4 text-${config.color}-600`} />
                                <span className="font-medium text-sm">{config.label}</span>
                                <Badge variant="secondary" className="ml-2">
                                  {changes.length}
                                </Badge>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="mt-2 border rounded-lg overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-slate-50">
                                    <TableHead className="w-[100px]">Tipo</TableHead>
                                    <TableHead className="w-[200px]">Campo</TableHead>
                                    <TableHead>Valor Anterior</TableHead>
                                    <TableHead>Valor Nuevo</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {changes.map((change, idx) => {
                                    const typeConfig = CHANGE_TYPE_CONFIG[change.change_type];
                                    const TypeIcon = typeConfig?.icon || Edit3;
                                    // Create unique key from field + change_type + category
                                    const changeKey = `${change.category}-${change.field}-${change.change_type}-${idx}`;

                                    return (
                                      <TableRow
                                        key={changeKey}
                                        className={
                                          change.change_type === "added"
                                            ? "bg-green-50/50"
                                            : change.change_type === "removed"
                                            ? "bg-red-50/50"
                                            : "bg-amber-50/30"
                                        }
                                        data-testid={`change-row-${idx}`}
                                      >
                                        <TableCell>
                                          <Badge
                                            variant="outline"
                                            className={`${typeConfig?.color} text-xs`}
                                          >
                                            <TypeIcon className="w-3 h-3 mr-1" />
                                            {typeConfig?.label}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="font-medium text-sm">
                                          {change.field}
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">
                                          {change.change_type === "added" ? (
                                            <span className="text-slate-400 italic">-</span>
                                          ) : (
                                            <span
                                              className={
                                                change.change_type === "removed"
                                                  ? "line-through text-red-600"
                                                  : ""
                                              }
                                            >
                                              {change.old_value}
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-sm max-w-[200px]">
                                          {change.change_type === "removed" ? (
                                            <span className="text-slate-400 italic">-</span>
                                          ) : (
                                            <span
                                              className={
                                                change.change_type === "added"
                                                  ? "text-green-700 font-medium"
                                                  : "text-amber-700"
                                              }
                                            >
                                              {change.new_value}
                                            </span>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    }
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">Sin diferencias</p>
                  <p className="text-sm">Las dos cotizaciones son idénticas</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State */}
        {!comparison && !loading && (
          <div className="flex-1 flex items-center justify-center text-slate-400 py-12">
            <div className="text-center">
              <GitCompare className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Selecciona dos cotizaciones</p>
              <p className="text-sm mt-1">
                Elige la versión anterior y la versión nueva para ver los cambios
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="w-10 h-10 mx-auto mb-3 animate-spin text-blue-500" />
              <p className="text-slate-500">Comparando cotizaciones...</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RevisionComparator;
